/**
 * Customer Form Modal
 * Create and edit customer accounts
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';

import { colorPalette } from '../../theme';
import { CAPABILITY_MAP } from '../../utils/statusTone';

const CustomerFormModal = ({ 
  open, 
  onClose, 
  onSubmit, 
  customer = null, 
  loading = false,
  error = null 
}) => {
  const isEditMode = !!customer;
  
  const [formData, setFormData] = useState({
    name: '',
    type: 'rental',
    status: 'Active',
    trackingMode: 'beacon',
    supplyMode: 'both',
    contact: {
      name: '',
      email: '',
      phone: '',
    },
    address: {
      street: '',
      city: '',
      state: '',
      zip: '',
    },
  });

  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name || '',
        type: customer.type || 'rental',
        status: customer.status || 'Active',
        trackingMode: customer.trackingMode || 'beacon',
        supplyMode: customer.supplyMode || 'both',
        contact: {
          name: customer.contact?.name || '',
          email: customer.contact?.email || '',
          phone: customer.contact?.phone || '',
        },
        address: {
          street: customer.address?.street || '',
          city: customer.address?.city || '',
          state: customer.address?.state || '',
          zip: customer.address?.zip || '',
        },
      });
    } else {
      // Reset form for new customer
      setFormData({
        name: '',
        type: 'rental',
        status: 'Active',
        trackingMode: 'beacon',
        supplyMode: 'both',
        contact: { name: '', email: '', phone: '' },
        address: { street: '', city: '', state: '', zip: '' },
      });
    }
  }, [customer, open]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value },
    }));
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      return;
    }
    
    const data = {
      ...formData,
      name: formData.name.trim(),
    };
    
    if (isEditMode && customer.id) {
      data.id = customer.id;
    }
    
    onSubmit(data);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditMode ? 'Edit Customer' : 'Add New Customer'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Basic Information
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label="Customer Name"
              fullWidth
              required
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                label="Type"
                onChange={(e) => handleChange('type', e.target.value)}
              >
                <MenuItem value="rental">Rental</MenuItem>
                <MenuItem value="utility">Utility</MenuItem>
                <MenuItem value="construction">Construction</MenuItem>
                <MenuItem value="energy">Energy</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => handleChange('status', e.target.value)}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Tracking & Supply Mode (Iteration 12) */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Tracking Configuration
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Tracking Mode</InputLabel>
              <Select
                value={formData.trackingMode}
                label="Tracking Mode"
                onChange={(e) => handleChange('trackingMode', e.target.value)}
              >
                <MenuItem value="beacon">{CAPABILITY_MAP.beacon.label}</MenuItem>
                <MenuItem value="manual">{CAPABILITY_MAP.manual.label}</MenuItem>
                <MenuItem value="hybrid">{CAPABILITY_MAP.hybrid.label}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Supply Mode</InputLabel>
              <Select
                value={formData.supplyMode}
                label="Supply Mode"
                onChange={(e) => handleChange('supplyMode', e.target.value)}
              >
                <MenuItem value="supply_only">Supply Only</MenuItem>
                <MenuItem value="demand_only">Demand Only</MenuItem>
                <MenuItem value="both">Both</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Contact Information
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              label="Contact Name"
              fullWidth
              value={formData.contact.name}
              onChange={(e) => handleNestedChange('contact', 'name', e.target.value)}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={formData.contact.email}
              onChange={(e) => handleNestedChange('contact', 'email', e.target.value)}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              label="Phone"
              fullWidth
              value={formData.contact.phone}
              onChange={(e) => handleNestedChange('contact', 'phone', e.target.value)}
            />
          </Grid>

          {/* Address */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Address
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label="Street Address"
              fullWidth
              value={formData.address.street}
              onChange={(e) => handleNestedChange('address', 'street', e.target.value)}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              label="City"
              fullWidth
              value={formData.address.city}
              onChange={(e) => handleNestedChange('address', 'city', e.target.value)}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              label="State"
              fullWidth
              value={formData.address.state}
              onChange={(e) => handleNestedChange('address', 'state', e.target.value)}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              label="ZIP Code"
              fullWidth
              value={formData.address.zip}
              onChange={(e) => handleNestedChange('address', 'zip', e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={loading || !formData.name.trim()}
          sx={{ backgroundColor: colorPalette.asseTracOrange }}
        >
          {loading ? <CircularProgress size={20} /> : (isEditMode ? 'Save Changes' : 'Create Customer')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerFormModal;
